
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

/**
 * @typedef {Object} BlogPost
 * @property {string} id
 * @property {string} title
 * @property {string} slug
 * @property {string} excerpt
 * @property {string} content
 * @property {string} image_url
 * @property {string} published_at
 */

export default function Blog() {
  /** @type {BlogPost[]} */
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      if (error) {
        console.error('Error loading posts:', error);
        setBlogPosts([]);
      } else if (data) {
        setBlogPosts(data);
      }
    } catch (err) {
      console.error('Unexpected error while loading posts:', err);
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black">
      <Navigation />

      {/* Hero Section with Title */}
      <section className="relative pt-24 sm:pt-32 pb-10 sm:pb-16 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center">
          <h1
            className="text-3xl sm:text-5xl md:text-6xl font-bold text-red-100 mb-4 sm:mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            CAPITAL PUNISHMENT BLOG
          </h1>
          <div className="w-20 sm:w-32 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent mx-auto"></div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-10 sm:py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          {loading ? (
            <div className="text-center py-12 sm:py-20">
              <i className="ri-loader-4-line text-3xl sm:text-4xl text-red-600 animate-spin"></i>
              <p className="mt-4 text-gray-400 text-sm sm:text-base">A carregar artigos...</p>
            </div>
          ) : blogPosts.length === 0 ? (
            <div className="text-center py-12 sm:py-20">
              <i className="ri-file-list-line text-4xl sm:text-6xl text-gray-600 mb-4"></i>
              <p className="text-gray-400 text-base sm:text-lg mb-4">
                Ainda não há artigos publicados
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {blogPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  onClick={handleLinkClick}
                >
                  <article className="group cursor-pointer transform hover:scale-105 transition-all duration-500">
                    <div className="bg-white rounded-2xl overflow-hidden shadow-lg border hover:shadow-2xl hover:shadow-red-500/10 transition-all duration-300">
                      <div className="relative h-44 sm:h-56 overflow-hidden">
                        {post.image_url ? (
                          <img
                            src={post.image_url}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <i className="ri-image-line text-3xl sm:text-4xl text-gray-400"></i>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      </div>

                      <div className="p-4 sm:p-6">
                        <div className="flex items-center gap-2 mb-2 sm:mb-3 text-xs text-gray-500">
                          <i className="ri-calendar-line"></i>
                          <span>
                            {post.published_at
                              ? new Date(post.published_at).toLocaleDateString(
                                  'pt-PT'
                                )
                              : ''}
                          </span>
                        </div>

                        <h3
                          className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-red-600 transition-colors leading-tight"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {post.title}
                        </h3>

                        <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed line-clamp-3">
                          {post.excerpt}
                        </p>

                        <div className="flex items-center justify-end">
                          <span className="flex items-center gap-2 text-red-600 text-xs sm:text-sm group-hover:text-red-700 transition-colors font-medium cursor-pointer whitespace-nowrap">
                            Ler mais
                            <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
